import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, LogIn } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/data/mockPeople";
import { URGENCY } from "@/data/openProblems";
import { useAuth, api, goToLogin } from "@/contexts/AuthContext";

export default function PostProblemModal({ open, onOpenChange, onCreated }) {
  const { user, loading } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [urgency, setUrgency] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      goToLogin("/problems");
      return;
    }
    if (!title.trim() || !category || !description.trim() || !urgency) {
      toast.error("Please fill out every field before posting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/open-problems", {
        title: title.trim(),
        category,
        description: description.trim(),
        desired_outcome: desiredOutcome.trim(),
        urgency,
      });
      toast.success("Problem posted.", {
        description: "Interested solvers can now reach out.",
      });
      setTitle("");
      setCategory("");
      setDescription("");
      setDesiredOutcome("");
      setUrgency("");
      onOpenChange(false);
      if (onCreated) onCreated(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't post the problem.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white border-l border-[#F3F4F6] p-8 overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-[#111827]">
            Post a problem
          </SheetTitle>
          <SheetDescription className="text-[#4B5563] font-body">
            Describe what you're trying to solve. Solvers can respond.
          </SheetDescription>
        </SheetHeader>

        {!user && !loading ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-[#4B5563]">
              You need an account to post a problem — one click with Google.
            </p>
            <Button
              onClick={() => goToLogin("/problems")}
              data-testid="pp-signin"
              className="w-full h-12 rounded-full bg-[#111827] hover:bg-black text-white font-semibold"
            >
              <LogIn size={16} className="mr-2" />
              Continue with Google
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                data-testid="pp-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Automate proposal writing"
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="pp-category"
                  className="mt-1.5 h-11 rounded-xl"
                >
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([...CATEGORIES, ...(category && !CATEGORIES.includes(category) ? [category] : [])])].map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                data-testid="pp-category-custom"
                placeholder="Or submit a category (e.g. Data pipelines)"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = e.currentTarget.value.trim();
                    if (v) {
                      setCategory(v);
                      e.currentTarget.value = "";
                    }
                  }
                }}
                className="mt-2 w-full h-10 rounded-xl px-3 text-sm bg-white ring-1 ring-dashed ring-[#B8FFDD] focus:ring-[#3FFFB6] outline-none placeholder:text-[#9CA3AF]"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                data-testid="pp-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What have you tried? What outcome are you after?"
                className="mt-1.5 min-h-32 rounded-xl"
              />
            </div>

            <div>
              <Label>What "solved" looks like</Label>
              <Textarea
                data-testid="pp-outcome"
                value={desiredOutcome}
                onChange={(e) => setDesiredOutcome(e.target.value)}
                placeholder="One or two sentences on what a resolved version of this looks like."
                className="mt-1.5 min-h-20 rounded-xl"
              />
            </div>

            <div>
              <Label>Urgency</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger
                  data-testid="pp-urgency"
                  className="mt-1.5 h-11 rounded-xl"
                >
                  <SelectValue placeholder="When do you need this?" />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              data-testid="pp-submit"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold text-base transition-colors"
            >
              <Send size={16} className="mr-2" />
              {submitting ? "Posting…" : "Post problem"}
            </Button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
